import React from 'react';

import { ISOBSERVER, ISSUPPORTER } from '../../constants/global';
import { useGlobalContext } from '../../context/global';
import FeedbackMine from '../feedback/MyFeedback';
import SubmitFeedback from '../feedback/SubmitFeedback';
import AgentChatPanel from '../help/chat/AgentChatPanel';
import ObserverChatPanel from '../help/chat/ObserverChatPanel';
import SupportChatPanel from '../help/chat/SupportChatPanel';
import HelpHub from '../help/HelpHub';
import MessagesInboxPanel from '../help/MessagesInboxPanel';
import ObserverPeersPanel from '../help/ObserverPeersPanel';
import ChildAgent from '../pages/child-agent';
import CompletionReceipt from '../pages/completion-receipt';
import ElectionEventsPanel from '../pages/election-day-pulse';
import ObserverChecklist from '../pages/observer-checklist';
import PuCancellationPanel from '../pages/pu-cancellation';
import ReportIssueStage from '../pages/report-issues';
import ReportFeedbackItem from '../pages/report-issues/feedback-item';
import ReportFeedbacks from '../pages/report-issues/feedbacks';
import ReportResultIssueStage from '../pages/report-result-issues';
import ReportsHub from '../pages/reports-hub';
import SupporterReportPanel from '../pages/supporter/SupporterReportPanel';
import UploadResult from '../pages/upload-result';
import PreviewMediaFile from '../pages/upload-result/preview-media-file';
import ViewSubmittedResultPage from '../pages/view-submited-result/page';
import RightSection, { type IGlobalRightSection } from '../utils/right-section';
import SideMenuMobile from './side-menu-mobile';

const GlobalRightSection = ({ ...props }) => {
	const globalContext = useGlobalContext();

	if (!globalContext) return <></>;

	const rsProps = globalContext.rsProps;

	if (!rsProps) return <></>;

	// Party-only panels never mount for an independent observer, whatever opened them: a new entry
	// point elsewhere cannot leak result upload, cancellations or the agent hierarchy to them.
	const isObserver = ISOBSERVER();
	// An Independent Supporter is the opposite case: an allow-list, not a deny-list. Everything else in
	// this registry is party or observer machinery, and several panels open api.vesr's hub, which
	// refuses supporters — so a panel added later stays closed to them until someone opts it in here.
	const isSupporter = ISSUPPORTER();
	const isView = (kind: Parameters<typeof rsProps.isView>[0], name: Parameters<typeof rsProps.isView>[1]) =>
		rsProps.isView(kind, name) &&
		!(isObserver && PARTY_ONLY_PANELS.has(name ?? '')) &&
		!(isSupporter && !SUPPORTER_PANELS.has(name ?? ''));

	return (
		<>
			<RightSection
				rsProps={rsProps}
				globalContext={globalContext}
			>
				{isView('view', 'report-feedbacks') ? <ReportFeedbacks /> : null}
				{isView('view', 'report-feedback-item') ? <ReportFeedbackItem /> : null}
				{isView('create', 'submit-feedback') ? <SubmitFeedback /> : null}
				{isView('view', 'feedback-mine') ? <FeedbackMine /> : null}
				{isView('view', 'child-agent') ? <ChildAgent /> : null}
				{isView('view', 'completion-receipt') ? <CompletionReceipt /> : null}
				{isView('view', 'election-events') ? <ElectionEventsPanel /> : null}
				{isView('view', 'help-hub') ? <HelpHub /> : null}
				{isView('view', 'support-chat') ? <SupportChatPanel /> : null}
				{isView('view', 'agent-chat') ? <AgentChatPanel /> : null}
				{isView('view', 'messages-inbox') ? <MessagesInboxPanel /> : null}
				{isView('view', 'side-menu-mobile') ? <SideMenuMobile /> : null}
				{isView('view', 'report-issues') ? <ReportIssueStage /> : null}
				{isView('create', 'reports-hub') ? <ReportsHub /> : null}
				{isSupporter && isView('create', 'supporter-report') ? <SupporterReportPanel /> : null}
				{isObserver && isView('view', 'observer-checklist') ? <ObserverChecklist /> : null}
				{isObserver && isView('view', 'observer-chat') ? <ObserverChatPanel /> : null}
				{isObserver && isView('view', 'observer-peers') ? <ObserverPeersPanel /> : null}
				{isView('create', 'pu-cancellation') ? <PuCancellationPanel /> : null}
				{isView('view', 'report-result-issues') ? <ReportResultIssueStage /> : null}
				{isView('view', 'preview-media-file') ? <PreviewMediaFile /> : null}
				{isView('view', 'view-submitted-result') ? <ViewSubmittedResultPage /> : null}
				{isObserver || isSupporter ? null : (
					<CompDisplay isView={isView('create', 'upload-result')}>
						<UploadResult />
					</CompDisplay>
				)}
			</RightSection>
		</>
	);
};

export default GlobalRightSection;

/** Panels that act on a party's data: result upload, cancellations, the agent hierarchy and its chat. */
export const PARTY_ONLY_PANELS = new Set<string>([
	'upload-result',
	'child-agent',
	'completion-receipt',
	'election-events',
	'pu-cancellation',
	'report-result-issues',
	'view-submitted-result',
	'agent-chat',
	'messages-inbox',
]);

/**
 * The only panels an Independent Supporter may open: their incident report, the mobile menu and app
 * feedback. Not the reports hub (it opens the VESR state-room hub), the help hub or any chat.
 */
export const SUPPORTER_PANELS = new Set<string>(['supporter-report', 'side-menu-mobile', 'submit-feedback', 'feedback-mine']);

const CompDisplay = ({ children, isView, globalContext }: IGlobalRightSection & { children?: any; isView: boolean }) => {
	const matchChild: any = React.Children.map(children, (child) => {
		if (child) return { ...child, props: { ...child.props, globalContext } };
		return child;
	});

	return <div className={isView ? '' : 'd-none'}>{matchChild}</div>;
};
